import { useState } from "react";
import { Gallery } from "react-grid-gallery";
import useSWR from "swr";
import { transformImgSrc } from "../Image";
import PhotoPreview from "../PhotoPreview";

export type PhotoDimensions = {
  width: number;
  height: number;
};

export type PhotoWithDimensions = PhotoDimensions & {
  src: string;
};

const getPhotoDimensions = async (
  src: string
): Promise<PhotoDimensions | undefined> => {
  const img = new Image();
  img.src = src;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      img.src = "";
      return;
    }, 20000);

    img.onload = () => {
      clearTimeout(timeout);
      resolve({
        width: img.width,
        height: img.height,
      });
    };
  });
};

const fetcher = async (photos: string[]): Promise<PhotoWithDimensions[]> => {
  const dimensions = await Promise.allSettled(
    photos.map((photo) => getPhotoDimensions(transformImgSrc(photo)))
  );

  const fulfilled: PromiseFulfilledResult<PhotoDimensions>[] =
    dimensions.filter((image) => image.status === "fulfilled") as any;

  return fulfilled.map((dimension, index) => ({
    ...dimension.value,
    src: transformImgSrc(photos[index]),
  }));
};

export default function PhotoGallery({ photoUrls }: { photoUrls: string[] }) {
  const [photoPreviewOpened, setPhotoPreviewOpened] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);

  const { data } = useSWR<PhotoWithDimensions[]>(photoUrls, fetcher);

  const images = data && [...data];

  if (!images) return <></>;

  return (
    <>
      <PhotoPreview
        imgUrl={photoPreviewUrl}
        open={photoPreviewOpened}
        setOpen={setPhotoPreviewOpened}
        dontTransform={true}
      />
      <Gallery
        images={images}
        enableImageSelection={false}
        onClick={(index, photo) => {
          setPhotoPreviewUrl(photo.src);
          setPhotoPreviewOpened(true);
        }}
      />
    </>
  );
}
